import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { newsService } from '../../../services/newsService';

const CATEGORY_STYLES = {
  ownership: 'bg-amber-50 text-amber-700 border-amber-100',
  acquisition: 'bg-rose-50 text-rose-700 border-rose-100',
  investment: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  general: 'bg-blue-50 text-blue-700 border-blue-100',
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const NewsSection = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    newsService
      .getLatestNews(12)
      .then((data) => {
        if (active) setArticles(data);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Nothing to show and not loading → hide the section entirely.
  if (!loading && articles.length === 0) return null;

  return (
    <section className="py-10 px-4 bg-gradient-to-b from-gray-900 to-gray-950">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="flex h-2.5 w-2.5 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
            </span>
            <h2 className="font-heading font-bold text-xl lg:text-2xl text-white flex items-center gap-2">
              <Icon name="Newspaper" size={22} className="text-amber-400" />
              In the News
            </h2>
          </div>
          <span className="text-xs text-gray-400 hidden sm:block">
            Who bought whom — follow the money
          </span>
        </div>

        {loading ? (
          <div className="flex gap-4 overflow-hidden">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="flex-shrink-0 w-[85vw] sm:w-[360px] h-40 rounded-xl bg-gray-800/60 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 scrollbar-hide"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {articles.map((article) => {
              const badge = CATEGORY_STYLES[article.category] || CATEGORY_STYLES.general;
              const CardTag = article.url ? 'a' : 'div';
              return (
                <CardTag
                  key={article.id}
                  {...(article.url
                    ? { href: article.url, target: '_blank', rel: 'noopener noreferrer' }
                    : {})}
                  className="group flex-shrink-0 w-[85vw] sm:w-[360px] bg-gray-800/60 hover:bg-gray-800 border border-gray-700/60 hover:border-amber-500/40 rounded-xl p-4 snap-start transition-all duration-200 flex flex-col"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${badge}`}
                    >
                      {article.category || 'news'}
                    </span>
                    <span className="text-[11px] text-gray-400">{formatDate(article.published_at)}</span>
                  </div>

                  <h3 className="font-semibold text-white text-sm leading-snug mb-2 group-hover:text-amber-300 transition-colors">
                    {article.title}
                  </h3>

                  {article.summary && (
                    <p className="text-xs text-gray-400 leading-relaxed line-clamp-3 flex-1">
                      {article.summary}
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-700/60">
                    <span className="text-[11px] text-gray-500 flex items-center gap-1 min-w-0">
                      {article.source && (
                        <>
                          <Icon name="Globe" size={11} />
                          <span className="truncate">{article.source}</span>
                        </>
                      )}
                    </span>
                    {article.url && (
                      <span className="text-[11px] text-amber-400 flex items-center gap-1 flex-shrink-0">
                        Read <Icon name="ArrowUpRight" size={12} />
                      </span>
                    )}
                  </div>
                </CardTag>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsSection;
